import {
  Button,
  Checkbox,
  Input,
  Progress,
  Radio,
  Result,
  Select,
  Space,
  Spin,
  message,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getPreScreenerQuestions, submitPreScreenerAnswer } from "../redux/slice/surveySlice";

// Above this many options a searchable dropdown is easier than a long list
const MAX_INLINE_OPTIONS = 12;

const Prescreener = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [step, setStep] = useState(0);
  const [value, setValue] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const quotaId = new URLSearchParams(location.search).get("quota_id");

  // Kept in local state (not the shared redux slice) so a response left over
  // from a previous visit can never trigger a redirect to the wrong survey.
  useEffect(() => {
    setData(null);
    setLoadError(null);
    setStep(0);
    dispatch(getPreScreenerQuestions(quotaId))
      .unwrap()
      .then((res) => setData(res?.data || { questions: [] }))
      .catch((err) => setLoadError(typeof err === "string" ? err : "Something went wrong"));
  }, [dispatch, quotaId]);

  const questions = useMemo(() => data?.questions || [], [data]);
  const question = questions[step];
  const done = data && step >= questions.length;

  // Every question answered → continue into the survey (backend start-link
  // redirects to Toluna), or back to the survey list if no survey was chosen.
  useEffect(() => {
    if (!done) return;
    setRedirecting(true);
    const timer = setTimeout(() => {
      if (data.startLink) {
        window.location.href = data.startLink;
      } else {
        navigate("/survey");
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [done, data, navigate]);

  const isAnswered =
    question?.answerType === "input"
      ? !!(value && String(value).trim())
      : question?.answerType === "MultiSelect"
        ? Array.isArray(value) && value.length > 0
        : value !== undefined && value !== null;

  const onNext = async () => {
    if (!isAnswered) {
      message.warning("Please answer this question to continue.");
      return;
    }
    const payload =
      question.answerType === "input"
        ? { questionId: question.id, value: String(value).trim() }
        : { questionId: question.id, answerIds: Array.isArray(value) ? value : [value] };

    setSaving(true);
    try {
      await dispatch(submitPreScreenerAnswer(payload)).unwrap();
      setValue(undefined);
      setStep((s) => s + 1);
    } catch (err) {
      message.error(typeof err === "string" ? err : "Failed to save your answer. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderInput = () => {
    const options = (question.answers || []).map((opt) => ({
      label: opt.displayName,
      value: opt.id,
    }));

    if (question.answerType === "input") {
      return (
        <Input
          size="large"
          placeholder="Please enter your response"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={onNext}
          maxLength={10}
        />
      );
    }

    if (question.answerType === "MultiSelect") {
      return options.length > MAX_INLINE_OPTIONS ? (
        <Select
          mode="multiple"
          size="large"
          style={{ width: "100%" }}
          options={options}
          value={value}
          onChange={setValue}
          optionFilterProp="label"
          placeholder="Select all that apply"
        />
      ) : (
        <Checkbox.Group value={value} onChange={setValue}>
          <Space direction="vertical">
            {options.map((o) => (
              <Checkbox key={o.value} value={o.value}>{o.label}</Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      );
    }

    return options.length > MAX_INLINE_OPTIONS ? (
      <Select
        showSearch
        size="large"
        style={{ width: "100%" }}
        options={options}
        value={value}
        onChange={setValue}
        optionFilterProp="label"
        placeholder="Please select"
      />
    ) : (
      <Radio.Group value={value} onChange={(e) => setValue(e.target.value)}>
        <Space direction="vertical">
          {options.map((o) => (
            <Radio key={o.value} value={o.value}>{o.label}</Radio>
          ))}
        </Space>
      </Radio.Group>
    );
  };

  if (loadError) {
    return (
      <Result
        status="warning"
        title="Couldn't load your profile questions"
        subTitle={loadError}
        extra={<Button type="primary" onClick={() => navigate("/survey")}>Back to surveys</Button>}
      />
    );
  }

  if (!data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spin tip="Loading..." size="large"><div style={{ minWidth: 300 }} /></Spin>
      </div>
    );
  }

  if (done || redirecting) {
    return (
      <Result
        status="success"
        title="Thanks! Your profile is complete."
        subTitle={data?.startLink ? "Taking you to the survey..." : "Taking you back to your surveys..."}
        extra={<Spin />}
      />
    );
  }

  if (!question) return null;

  // Overall progress across the whole profile, including questions answered earlier
  const total = data.total || questions.length;
  const answeredBefore = data.answered || 0;
  const current = answeredBefore + step + 1;

  return (
    <div className="container">
      <div className="row" style={{ display: "flex", justifyContent: "center" }}>
        <div className="col-md-8">
          <div className="prescreener-page" style={{ padding: "20px" }}>
            <h3>{data.name || "Complete your profile"}</h3>
            <p style={{ color: "#888" }}>
              A few quick questions so we can match you with the right surveys.
            </p>
            <Progress percent={Math.round(((current - 1) / total) * 100)} />
            <p style={{ marginTop: 8, color: "#888" }}>
              Question {step + 1} of {questions.length}
            </p>

            <h5 style={{ margin: "20px 0" }}>{question.displayName}</h5>
            <div style={{ marginBottom: 24 }}>{renderInput()}</div>

            <Button type="primary" size="large" loading={saving} disabled={!isAnswered} onClick={onNext}>
              {step === questions.length - 1 ? "Finish" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Prescreener;
